import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useSimilarJobsAndRecos } from '@xing-com/crate-jobs-hooks';
import { GridContainer } from '@xing-com/grid';
import { NotOnSmall, OnlyOnSmall } from '@xing-com/layout-primitives';
import { space3XL, spaceL, spaceXXL } from '@xing-com/tokens';
import { Headline } from '@xing-com/typography';

import { RecommendationsCarousel } from './recommendations-carousel';
import { RecommendationsGrid } from './recommendations-grid';
import { RecommendationsSkeleton } from './recommendations-skeleton';

const WideMedia = styled(NotOnSmall)`
  margin: ${space3XL} ${spaceL};
`;

const SmallMedia = styled(OnlyOnSmall)<{ $hasSavedJobs?: boolean }>`
  margin: ${spaceL};
  margin-top: ${({ $hasSavedJobs }) => ($hasSavedJobs ? spaceXXL : 0)};
  margin-bottom: ${spaceXXL};
`;

const StyledHeadline = styled(Headline)`
  margin-bottom: ${spaceXXL};
`;

type Props = {
  jobId?: string;
};

export const SimilarJobsAndRecos: React.FC<Props> = ({ jobId }) => {
  const { loading, similarJobs, recommendations } = useSimilarJobsAndRecos({
    limit: 9,
    jobId,
  });
  const jobs = similarJobs.length > 0 ? similarJobs : recommendations;
  const headlineCopy =
    similarJobs.length > 0
      ? 'JOBS_MYJOBS_ERROR_PAGE_SIMILAR_JOBS_TITLE'
      : 'JOBS_MYJOBS_ERROR_PAGE_RECOMMENDATION_TITLE';

  if (jobs.length === 0 && !loading) {
    return null;
  }

  return (
    <>
      <SmallMedia>
        {loading ? (
          <RecommendationsSkeleton type="carousel" />
        ) : (
          <>
            <StyledHeadline size="xxlarge">
              <FormattedMessage id={headlineCopy} />
            </StyledHeadline>
            <RecommendationsCarousel recommendations={jobs} />
          </>
        )}
      </SmallMedia>
      <WideMedia>
        {loading ? (
          <RecommendationsSkeleton type="grid" />
        ) : (
          <>
            <GridContainer variant="two-col">
              <StyledHeadline size="xxlarge">
                <FormattedMessage id={headlineCopy} />
              </StyledHeadline>
            </GridContainer>
            <RecommendationsGrid recommendations={jobs} />
          </>
        )}
      </WideMedia>
    </>
  );
};
